/**
 * ChoicesEditor — jurisdiction radio for choice modules (Code of Conduct,
 * etc.). Rendered at the top of the col-2 Notes panel for the active
 * section. Selecting a choice calls `onChoiceChange`, which sets the
 * module's saved value and (per plan §3.1.4) inserts the boilerplate into
 * the editor doc.
 *
 * When the parent narrows `choices` to a single entry (case jurisdiction
 * already decided at case creation), the radio collapses into a read-only
 * descriptor — there's nothing for the user to pick.
 *
 * Lifted from the retiring SectionEditorPanel so both it and NotesPanel
 * can share one source.
 */
import { useEffect } from "react";

import { Info } from "lucide-react";

import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";

import { cn } from "@utils/ui";

import type { ModuleChoice } from "../editor/utils";

export const ChoicesEditor = ({
  activeSection,
  choices,
  choiceByModule,
  onChoiceChange,
}: {
  activeSection: string;
  choices: ModuleChoice[];
  choiceByModule: Record<string, string>;
  onChoiceChange: (moduleName: string, choiceKey: string) => void;
}) => {
  const selectedKey = choiceByModule[activeSection] ?? choices[0]?.key;
  const isSingleChoice = choices.length === 1;
  const onlyChoice = choices[0];

  // When the case's jurisdiction has already narrowed the list to one
  // option, persist that choice into the module state so the draft
  // payload carries it without the user clicking anything.
  useEffect(() => {
    if (!isSingleChoice || !onlyChoice) return;
    if (choiceByModule[activeSection] === onlyChoice.key) return;
    onChoiceChange(activeSection, onlyChoice.key);
  }, [isSingleChoice, onlyChoice, activeSection, choiceByModule, onChoiceChange]);

  if (isSingleChoice && onlyChoice) {
    return (
      <div className="mb-4">
        <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2 text-sm text-foreground">
          <Info className="mt-0.5 size-4 shrink-0 text-blue-600" />
          <span>
            Based on the case's location, the {onlyChoice.display_name} Code of
            Conduct will be inserted into the report.
          </span>
        </div>
        <div className="rounded-lg border border-primary bg-primary-50/40 p-3 text-sm">
          <div className="font-semibold">{onlyChoice.display_name}</div>
          {onlyChoice.description ? (
            <p className="mt-1 text-foreground-muted">{onlyChoice.description}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2 text-sm text-foreground">
        <Info className="mt-0.5 size-4 shrink-0 text-blue-600" />
        <span>
          Select the jurisdiction whose Expert Witness Code of Conduct applies. The
          selected wording is inserted into the report.
        </span>
      </div>
      <RadioGroup
        value={selectedKey}
        onValueChange={(v) => onChoiceChange(activeSection, v)}
        className="gap-2"
      >
        {choices.map((choice) => (
          <ChoiceOption
            key={choice.key}
            activeSection={activeSection}
            choice={choice}
            isSelected={selectedKey === choice.key}
          />
        ))}
      </RadioGroup>
    </div>
  );
};

const ChoiceOption = ({
  activeSection,
  choice,
  isSelected,
}: {
  activeSection: string;
  choice: ModuleChoice;
  isSelected: boolean;
}) => {
  const id = `choice-${activeSection}-${choice.key}`;
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
        "hover:bg-primary-50/30",
        isSelected && "border-primary bg-primary-50/40"
      )}
    >
      <RadioGroupItem id={id} value={choice.key} className="mt-0.5" />
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-semibold">{choice.display_name}</div>
        {choice.description ? (
          <p className="mt-1 text-foreground-muted">{choice.description}</p>
        ) : null}
      </div>
    </label>
  );
};
